"use client";

import * as React from "react";
import dynamic from "next/dynamic";
import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import {
  BookOpen,
  BriefcaseBusiness,
  CalendarDays,
  Coffee,
  HeartPulse,
  LayoutPanelTop,
  Layers3,
  Menu,
  Newspaper,
  UserRound,
  X,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { getNeutralTrialSignupHref } from "@/lib/content/pricing";
import {
  primaryNavItems,
  type SiteNavItem,
  type SiteNavLink,
} from "@/lib/content/navigation";
import { cn } from "@/lib/utils";
import { MarketingTrackedLink } from "@/components/site/primitives/marketing-tracked-link";

const HOME_PATH = "/";
const PRODUCT_MENU_NAME = "Product";

const ProductMegaMenu = dynamic(
  () =>
    import("@/components/site/layout/product-mega-menu").then(
      (module) => module.ProductMegaMenu,
    ),
  {
    ssr: false,
  },
);

function preloadProductMegaMenu() {
  void import("@/components/site/layout/product-mega-menu");
}

function matchesRoute(pathname: string, hrefPath: string) {
  if (hrefPath === HOME_PATH) {
    return pathname === HOME_PATH;
  }

  return pathname === hrefPath || pathname.startsWith(`${hrefPath}/`);
}

export function SiteHeader() {
  const pathname = usePathname();
  const navRef = React.useRef<HTMLElement>(null);
  const [menuState, setMenuState] = React.useState(false);
  const [isScrolled, setIsScrolled] = React.useState(false);
  const [isOverDarkSurface, setIsOverDarkSurface] = React.useState(
    pathname === "/compare",
  );
  const [openMenu, setOpenMenu] = React.useState<string | null>(null);

  const isAnyHrefActive = React.useCallback(
    (hrefs: readonly string[]) =>
      hrefs.some((href) => matchesRoute(pathname, href)),
    [pathname],
  );

  const handleMobileMenuClick = React.useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      const target = event.target;

      if (!(target instanceof Element)) {
        return;
      }

      if (target.closest("a[href]")) {
        setMenuState(false);
      }
    },
    [],
  );

  React.useEffect(() => {
    const darkSurface = document.querySelector<HTMLElement>(
      '[data-site-header-surface="dark"]',
    );

    const updateHeaderState = () => {
      setIsScrolled(window.scrollY > 50);

      const headerBottom = navRef.current?.getBoundingClientRect().bottom;
      const surfaceBounds = darkSurface?.getBoundingClientRect();

      setIsOverDarkSurface(
        Boolean(
          headerBottom !== undefined &&
            surfaceBounds &&
            surfaceBounds.top < headerBottom &&
            surfaceBounds.bottom > headerBottom,
        ),
      );
    };

    updateHeaderState();
    window.addEventListener("scroll", updateHeaderState, { passive: true });
    window.addEventListener("resize", updateHeaderState);

    return () => {
      window.removeEventListener("scroll", updateHeaderState);
      window.removeEventListener("resize", updateHeaderState);
    };
  }, [pathname]);

  React.useEffect(() => {
    if (!menuState) {
      return;
    }

    const closeMenu = () => setMenuState(false);
    window.addEventListener("resize", closeMenu);

    return () => window.removeEventListener("resize", closeMenu);
  }, [menuState]);

  React.useEffect(() => {
    if (!menuState && !openMenu) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setMenuState(false);
        setOpenMenu(null);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [menuState, openMenu]);

  return (
    <header>
      <nav
        ref={navRef}
        data-state={menuState ? "active" : undefined}
        data-scrolled={isScrolled ? "true" : undefined}
        className="group fixed z-40 w-full px-2"
      >
        <div
          className={cn(
            "mx-auto mt-2 max-w-6xl rounded-2xl border border-border/80 px-6 transition-[max-width,background-color,border-color,box-shadow,backdrop-filter,padding] duration-300 ease-[var(--ease-out)] lg:px-12",
            isOverDarkSurface || menuState
              ? "bg-background backdrop-blur-none"
              : "bg-background/70 backdrop-blur-lg",
            menuState &&
              "rounded-[1.75rem] shadow-[0_18px_50px_-28px_rgba(15,23,42,0.28)] lg:shadow-none",
            isScrolled &&
              "max-w-5xl lg:px-6",
          )}
        >
          <div className="relative flex flex-wrap items-center justify-between gap-x-6 gap-y-0 py-3 lg:grid lg:min-h-16 lg:grid-cols-[max-content_minmax(0,1fr)_max-content] lg:items-center lg:gap-4 lg:py-3.5">
            <div className="flex w-full items-center justify-between gap-3 lg:w-auto">
              <Link
                href="/"
                aria-label="Cooperly home"
                className="flex h-9 items-center"
              >
                <Image
                  src="/cooperly/cooperly_logo.svg"
                  alt="Cooperly logo"
                  width={120}
                  height={28}
                  priority
                  className="h-auto w-[120px]"
                />
              </Link>

              <div className="flex items-center gap-2 lg:hidden">
                <Button
                  asChild
                  variant="outline"
                  size="sm"
                  className="group-data-[state=active]:hidden lg:hidden"
                >
                  <MarketingTrackedLink
                    href="https://cal.eu/cooperly/demo"
                    target="_blank"
                    rel="noopener noreferrer"
                    eventName="demo_cta_clicked"
                    eventProperties={{
                      cta_id: "header_book_demo",
                      placement: "mobile_topbar",
                      route_name: "site_header",
                    }}
                  >
                    <span>Book a demo</span>
                  </MarketingTrackedLink>
                </Button>

                <button
                  type="button"
                  onClick={() => setMenuState((prev) => !prev)}
                  aria-controls="site-mobile-menu"
                  aria-expanded={menuState}
                  aria-label={menuState ? "Close Menu" : "Open Menu"}
                  className="site-pressable relative z-20 -m-2.5 -mr-4 block rounded-xl p-2.5 active:scale-[0.94] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                >
                  <Menu className="m-auto size-6 transition-[transform,opacity] duration-200 ease-[var(--ease-out)] group-data-[state=active]:scale-0 group-data-[state=active]:rotate-180 group-data-[state=active]:opacity-0" />
                  <X className="absolute inset-0 m-auto size-6 -rotate-180 scale-0 opacity-0 transition-[transform,opacity] duration-200 ease-[var(--ease-out)] group-data-[state=active]:scale-100 group-data-[state=active]:rotate-0 group-data-[state=active]:opacity-100" />
                </button>
              </div>
            </div>

            <div className="hidden lg:flex lg:justify-center">
              <ul className="flex h-9 items-center gap-2 text-sm xl:gap-3">
                {primaryNavItems.map((item) => {
                  const isActive = isAnyHrefActive(item.matchPaths);
                  const isOpen = openMenu === item.name;
                  const itemIcon = "icon" in item ? item.icon : undefined;

                  return (
                    <li
                      key={item.name}
                      className="relative"
                      onMouseEnter={() => {
                        if (item.kind === "menu") {
                          if (item.name === PRODUCT_MENU_NAME) {
                            preloadProductMegaMenu();
                          }
                          setOpenMenu(item.name);
                        }
                      }}
                      onMouseLeave={() => {
                        if (item.kind === "menu") {
                          setOpenMenu((current) =>
                            current === item.name ? null : current,
                          );
                        }
                      }}
                    >
                      {item.kind === "menu" ? (
                        <>
                          <button
                            type="button"
                            aria-haspopup="menu"
                            aria-expanded={isOpen}
                            onFocus={() => {
                              if (item.name === PRODUCT_MENU_NAME) {
                                preloadProductMegaMenu();
                              }
                              setOpenMenu(item.name);
                            }}
                            className={cn(
                              "site-pressable rounded-full px-4 py-2 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground",
                              (isOpen || isActive) &&
                                "bg-muted/65 text-foreground",
                            )}
                          >
                            {item.name}
                          </button>

                          <DesktopMegaMenu
                            item={item}
                            open={isOpen}
                            onClose={() => setOpenMenu(null)}
                          />
                        </>
                      ) : (
                        <Link
                          href={item.href}
                          aria-current={isActive ? "page" : undefined}
                          className={cn(
                            "site-pressable relative flex items-center gap-1.5 whitespace-nowrap px-3 py-2 text-sm after:absolute after:inset-x-3 after:-bottom-px after:h-px after:origin-center after:scale-x-0 after:bg-foreground/85 after:transition-transform after:duration-200 after:ease-[var(--ease-out)] hover:text-foreground hover:after:scale-x-100",
                            isActive
                              ? "text-foreground after:scale-x-100"
                              : "text-muted-foreground",
                          )}
                        >
                          {itemIcon === "manifesto" ? (
                            <BookOpen className="size-3.5 shrink-0" />
                          ) : itemIcon === "insights" ? (
                            <Newspaper className="size-3.5 shrink-0" />
                          ) : null}
                          <span>{item.name}</span>
                        </Link>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>

            <div className="hidden lg:flex lg:items-center lg:justify-end lg:gap-3">
              <div className="flex flex-col space-y-3 sm:flex-row sm:items-center sm:gap-3 sm:space-y-0">
                <Button
                  asChild
                  variant="outline"
                  size="sm"
                  className="header-scroll-hidden lg:inline-flex"
                >
                  <MarketingTrackedLink
                    href="https://cal.eu/cooperly/demo"
                    target="_blank"
                    rel="noopener noreferrer"
                    eventName="demo_cta_clicked"
                    eventProperties={{
                      cta_id: "header_book_demo",
                      placement: "desktop",
                      route_name: "site_header",
                    }}
                  >
                    <span>Book a demo</span>
                  </MarketingTrackedLink>
                </Button>
                <Button asChild size="sm" className="header-scroll-hidden">
                  <MarketingTrackedLink
                    href={getNeutralTrialSignupHref("header_start_trial")}
                    eventName="trial_cta_clicked"
                    eventProperties={{
                      cta_id: "header_start_trial",
                      placement: "desktop",
                      route_name: "site_header",
                    }}
                  >
                    <span>Start free trial</span>
                  </MarketingTrackedLink>
                </Button>
                <Button
                  asChild
                  variant="outline"
                  size="icon"
                  className="header-scroll-visible hidden size-9 rounded-full lg:inline-flex"
                >
                  <MarketingTrackedLink
                    href="https://cal.eu/cooperly/demo"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Book a demo"
                    eventName="demo_cta_clicked"
                    eventProperties={{
                      cta_id: "header_book_demo",
                      placement: "compact",
                      route_name: "site_header",
                    }}
                  >
                    <CalendarDays className="size-4" />
                  </MarketingTrackedLink>
                </Button>
                <Button
                  asChild
                  size="sm"
                  className="header-scroll-visible hidden lg:inline-flex"
                >
                  <MarketingTrackedLink
                    href={getNeutralTrialSignupHref("header_start_trial")}
                    eventName="trial_cta_clicked"
                    eventProperties={{
                      cta_id: "header_start_trial",
                      placement: "compact",
                      route_name: "site_header",
                    }}
                  >
                    <span>Start free trial</span>
                  </MarketingTrackedLink>
                </Button>
              </div>
            </div>

            <div
              id="site-mobile-menu"
              onClick={handleMobileMenuClick}
              className={cn(
                "site-scrollbar-auto w-full transition-[max-height,opacity,transform] duration-300 ease-[var(--ease-out)] lg:hidden",
                menuState
                  ? "mt-5 max-h-[calc(100dvh-5.5rem)] translate-y-0 overflow-y-auto opacity-100"
                  : "pointer-events-none max-h-0 -translate-y-2 overflow-hidden opacity-0",
              )}
            >
              <div className="flex flex-col gap-4 pb-2">
                <MobileNavMenu
                  items={primaryNavItems}
                  isAnyHrefActive={isAnyHrefActive}
                />

                <div className="grid w-full grid-cols-[minmax(0,1fr)_auto] gap-2">
                  <Button
                    asChild
                    variant="outline"
                    size="sm"
                    className="h-11 rounded-xl text-base"
                  >
                    <MarketingTrackedLink
                      href="https://cal.eu/cooperly/demo"
                      target="_blank"
                      rel="noopener noreferrer"
                      eventName="demo_cta_clicked"
                      eventProperties={{
                        cta_id: "header_book_demo",
                        placement: "mobile",
                        route_name: "site_header",
                      }}
                    >
                      <span>Book a demo</span>
                    </MarketingTrackedLink>
                  </Button>
                  <Button
                    asChild
                    size="sm"
                    className="h-11 rounded-xl px-5 text-base"
                  >
                    <MarketingTrackedLink
                      href={getNeutralTrialSignupHref("header_start_trial")}
                      eventName="trial_cta_clicked"
                      eventProperties={{
                        cta_id: "header_start_trial",
                        placement: "mobile",
                        route_name: "site_header",
                      }}
                    >
                      <span>Start free trial</span>
                    </MarketingTrackedLink>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </nav>
    </header>
  );
}

function MobileNavMenu({
  items,
  isAnyHrefActive,
}: {
  items: readonly SiteNavItem[];
  isAnyHrefActive: (hrefs: readonly string[]) => boolean;
}) {
  return (
    <ul className="space-y-1">
      {items.map((item, index) => (
        <li key={item.name}>
          <MobileTopLevelLink
            item={item}
            active={isAnyHrefActive(item.matchPaths)}
            first={index === 0}
          />
          {item.kind === "menu" ? (
            <ul className="pl-5">
              {item.menuSections
                .flatMap((section) => section.links)
                .filter((link) => link.showInMobileMenu)
                .map((link) => (
                  <li key={link.href}>
                    <Link
                      href={link.href}
                      aria-current={
                        isAnyHrefActive([link.href]) ? "page" : undefined
                      }
                      className="site-pressable inline-flex min-h-11 items-center py-1.5 text-base text-muted-foreground transition-colors hover:text-foreground aria-[current=page]:text-foreground"
                    >
                      {link.title}
                    </Link>
                  </li>
                ))}
            </ul>
          ) : null}
        </li>
      ))}
    </ul>
  );
}

function MobileTopLevelLink({
  item,
  active,
  first,
}: {
  item: SiteNavItem;
  active: boolean;
  first: boolean;
}) {
  const itemIcon = item.kind === "link" ? item.icon : undefined;
  const href =
    item.kind === "link"
      ? item.href
      : (item.menuSections[0]?.links[0]?.href ?? "/");

  return (
    <Link
      href={href}
      aria-current={active ? "page" : undefined}
      className={cn(
        "site-pressable inline-flex min-h-10 items-center gap-3 py-1.5 text-[1.05rem] font-medium leading-7 transition-colors hover:text-foreground",
        first && "pt-0",
        active ? "text-foreground" : "text-muted-foreground",
      )}
    >
      {itemIcon === "manifesto" ? (
        <BookOpen className="size-4 shrink-0" />
      ) : itemIcon === "insights" ? (
        <Newspaper className="size-4 shrink-0" />
      ) : null}
      <span>{item.name}</span>
    </Link>
  );
}

function DesktopMegaMenu({
  item,
  open,
  onClose,
}: {
  item: Extract<SiteNavItem, { kind: "menu" }>;
  open: boolean;
  onClose: () => void;
}) {
  return (
    <div
      className={cn(
        "pointer-events-none absolute left-1/2 top-full z-40 -translate-x-1/2 pt-4 opacity-0 transition-[opacity,transform] duration-200 ease-[var(--ease-out)]",
        item.menuWidth ?? "w-[min(48rem,calc(100vw-3rem))]",
        open && "pointer-events-auto translate-y-0 opacity-100",
        !open && "translate-y-2",
      )}
    >
      <div className="overflow-hidden rounded-[1.45rem] border border-border/80 bg-background/98 shadow-[0_24px_80px_-44px_rgba(15,23,42,0.22)] backdrop-blur-xl">
        {open && item.name === PRODUCT_MENU_NAME ? (
          <ProductMegaMenu item={item} onNavigate={onClose} />
        ) : open ? (
          <div
            className="grid overflow-hidden divide-x divide-border/60"
            style={{
              gridTemplateColumns:
                item.menuGridTemplateColumns ??
                `repeat(${item.menuSections.length}, minmax(0, 1fr))`,
            }}
          >
            {item.menuSections.map((section) => (
              <div
                key={
                  section.label ??
                  section.links.map((link) => link.title).join("-")
                }
                className="bg-background px-4 py-4 md:px-4 md:py-4"
              >
                {section.label ? (
                  <p className="site-eyebrow text-foreground/62">
                    {section.label}
                  </p>
                ) : null}
                <div className={cn("space-y-0.5", section.label && "mt-3")}>
                  {section.links.map((link) => (
                    <DesktopMegaMenuLink
                      key={link.href}
                      link={link}
                      onNavigate={onClose}
                      compact={Boolean(item.compact)}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function DesktopMegaMenuLink({
  link,
  onNavigate,
  compact,
}: {
  link: SiteNavLink;
  onNavigate: () => void;
  compact?: boolean;
}) {
  return (
    <Link
      href={link.href}
      onClick={onNavigate}
      className={cn(
        "group block rounded-[0.95rem] transition-colors duration-200 hover:bg-muted/35",
        compact ? "px-2.5 py-2.5" : "px-2.5 py-2.5",
      )}
    >
      <div className="flex items-start gap-2.5">
        {renderMenuIcon(link.icon)}
        <div className="max-w-[16.5rem] space-y-1">
          <p
            className={cn(
              "font-medium leading-6 text-foreground",
              compact ? "text-[0.95rem]" : "text-base",
            )}
          >
            {link.title}
          </p>
          {link.description ? (
            <p
              className={cn(
                "text-pretty text-muted-foreground",
                compact ? "text-[0.84rem] leading-5" : "text-sm leading-6",
              )}
            >
              {link.description}
            </p>
          ) : null}
        </div>
      </div>
    </Link>
  );
}

function renderMenuIcon(icon: SiteNavLink["icon"] | undefined, mobile = false) {
  const sizeClass = mobile ? "size-4" : "size-3.5";

  const Icon =
    icon === "overview"
      ? LayoutPanelTop
      : icon === "coop-profile"
        ? UserRound
        : icon === "fundamentals"
          ? Layers3
          : icon === "pulse"
            ? HeartPulse
            : icon === "candidates"
              ? BriefcaseBusiness
              : icon === "random-coffee"
                ? Coffee
                : null;

  if (!Icon) {
    return null;
  }

  return (
    <span className="mt-[0.18rem] flex size-5 shrink-0 items-center justify-center text-foreground/68">
      <Icon className={cn(sizeClass, "text-foreground/78")} />
    </span>
  );
}
